/**
 * SystemOverview Component
 *
 * This component manages the state for the SystemOverview page, allowing users
 * to switch between different widgets based on the permissions received
 * from GET_WIDGETS_TYPE API.
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import HomePage from "./SystemOverview/Home";
import Navigationbar from "./Navigationbar";
import CrowdMonitoring from "./SystemOverview/CrowdMonitoring";
// import GateDetection from "./SystemOverview/GateDetection";
// import LoadingSession from "./SystemOverview/LoadingSession";
// import DarshanETA from "./SystemOverview/DarshanETA";

import {
  getFreshToken,
  get,
} from "../custom_libraries/serverRequests";
import { Tabs, Tab, Box, Typography, useMediaQuery } from "@mui/material";

import globalVariables from "../custom_libraries/globalVariables";
import urls from "../custom_libraries/urls";

const SystemOverview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { menu } = useParams();

  // Menu state
  const [activeMenu, setActiveMenu] = useState(menu || "home");
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  // Loading state
  const [loading, setLoading] = useState(true);

  // API data
  const [dataAvailable, setDataAvailable] = useState(null);
  const [matrixData, setMatrixData] = useState(null);

  // Available widgets/types received from API
  const [availableTypes, setAvailableTypes] = useState([]);

  // Currently selected widget/tab
  const [activeType, setActiveType] = useState(null);

  /**
   * Sync active menu with URL parameter
   */
  useEffect(() => {
    validateSignedIn();

    setActiveMenu(menu || "home");

    // Fetch available widget types
    getWidgetTypes();
  }, [menu]);

  /**
   * Handle menu click
   */
  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
  };

  /**
   * Handle submenu click
   */
  const handleSubmenuClick = (submenu) => {
    setActiveSubmenu(submenu);
  };

  /**
   * Handle widget/tab selection
   */
  const handleTypeChange = (type) => {
    setActiveType(type);
  };

  /**
   * Validate user authentication
   */
  const validateSignedIn = async () => {
    const is_fresh_token = await getFreshToken();

    if (!is_fresh_token) {
      navigate("/");
      return;
    }
  };

  /**
   * Convert API permission name to internal widget type
   *
   * Example:
   * Camera_Health   -> camera_health
   * Loading_Session  -> loading_session
   * Darshan_ETA      -> darshan_eta
   */
  const getWidgetType = (permissionName) => {
    if (!permissionName) {
      return null;
    }

    return permissionName.trim().toLowerCase();
  };

  /**
   * Fetch available widget types from API
   *
   * Expected API response:
   *
   * {
   *   success: true,
   *   permissions: [
   *     {
   *       enabled: true,
   *       permission_code: "101",
   *       permission_name: "Camera_Health"
   *     },
   *     {
   *       enabled: false,
   *       permission_code: "103",
   *       permission_name: "Loading_Session"
   *     },
   *     {
   *       enabled: true,
   *       permission_code: "104",
   *       permission_name: "Darshan_ETA"
   *     }
   *   ]
   * }
   */
const getWidgetTypes = async () => {
  const user_type = localStorage.getItem(globalVariables.USER_TYPE);

  if (user_type === "CustomerUser") {
        setAvailableTypes(["darshan_eta"]);
        setActiveType("darshan_eta");
        setDataAvailable(true); 
        setLoading(false);
        return;
  }
  else {
  try {
    setLoading(true);

    const access_token = localStorage.getItem(
      globalVariables.AUTH_TOKEN
    );

    const headers = {
      "content-type": "application/json",
      Authorization: `Bearer ${access_token}`,
    };

    const response = await get(urls.GET_WIDGETS_TYPE, headers);

    console.log("API Response for widget types:", response);

    if (
      response?.success === true &&
      Array.isArray(response?.permissions) &&
      response.permissions.length > 0
    ) {
      // API already returns only the permissions that are enabled
      const types = response.permissions
        .map((permission) =>
          permission.permission_name?.trim().toLowerCase()
        )
        .filter(Boolean);

      console.log("Available widget types:", types);

      setAvailableTypes(types);

      // Camera Health should be selected by default if available
      if (types.includes("camera_health")) {
        setActiveType("camera_health");
      }
       else {
        // Otherwise select the first available widget
        setActiveType(types[0]);
      }

      setDataAvailable(true);
    } else {
      setAvailableTypes([]);
      setActiveType(null);
      setDataAvailable(false);
    }
  } catch (error) {
    console.error("Error fetching widget types:", error);

    setAvailableTypes([]);
    setActiveType(null);
    setDataAvailable(false);
  } finally {
    setLoading(false);
  }
}
};

  /**
   * Render widget based on selected type
   */
  const renderWidget = () => {
    switch (activeType) {
      case "camera_health":
        return <HomePage />;

      case "darshan_eta":
        return <CrowdMonitoring />;

      // Uncomment when these components are available

      // case "gate_detection":
      //   return <GateDetection />;

      // case "loading_session":
      //   return <LoadingSession />;

      // case "darshan_eta":
      //   return <DarshanETA />;

      default:
        return null;
    }
  };

  /**
   * Get display label for widget type
   */
  const getTypeLabel = (type) => {
    const labels = {
      camera_health: "Camera Health",
      crowd_monitoring: "Crowd Monitoring",
      gate_detection: "Gate Detection",
      loading_session: "Loading Session",
      darshan_eta: "Darshan ETA",
    };

    return (
      labels[type] ||
      type
        ?.replace(/_/g, " ")
        ?.replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  /**
   * Render tab button
   */
  const renderTab = (type) => {
  return (
    <Tab
      key={type}
      value={type}
      label={getTypeLabel(type)}
      sx={{
        minWidth: 150,
        fontWeight: 500,
        color: "#ffffff",
        textTransform: "none",
        "&.Mui-selected": {
          backgroundColor: "#757f8a",
          color: "#ffffff",
          fontWeight: 600,
        },
      }}
    />
  );
};

return (
  <div
    style={{
      width: "100%",
      minHeight: "100vh",
    }}
  >
    {/* Navigation Bar */}
    <Navigationbar />

    {/* Main System Overview */}
    <div
      className="system-overview-content-container"
      style={{
        width: "100%",
        paddingTop: "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginLeft: "3%",
      }}
    >
      {/* =========================
          TABS SECTION
      ========================== */}
      <Box
        sx={{
          position: "sticky",
          // top: "60px",
          // zIndex: 100,
          width: "90%",
          backgroundColor: "#2c2c2c",
          borderBottom: "1px solid #555",
        }}
      >
        <Tabs
          value={activeType || false}
          onChange={(event, newValue) => {
            handleTypeChange(newValue);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          textColor="inherit"
          sx={{
            minHeight: "48px",

            "& .MuiTabs-flexContainer": {
              display: "flex",
              flexDirection: "row",
            },

            "& .MuiTabs-indicator": {
              backgroundColor: "#ffffff",
              height: "3px",
            },
          }}
        >
          {availableTypes.map((type) => renderTab(type))}
        </Tabs>
      </Box>

      {/* =========================
          SELECTED WIDGET
      ========================== */}
      <div
        className="system-overview-page-content"
        style={{
          width: "96%",
          paddingTop: "10px",
        }}
      >
        {loading ? (
          <h2
            style={{
              marginLeft: "15%",
              display: "flex",
              alignContent: "center",
            }}
          >
            Loading...
          </h2>
        ) : (
          renderWidget()
        )}
      </div>
    </div>
  </div>
);
};

export default SystemOverview;